import { useApolloClient, useMutation } from '@apollo/client'
import { differenceInCalendarDays } from 'date-fns'
import { Loader } from 'lucide-react'
import { useState, useTransition } from 'react'
import { toast } from 'sonner'
import {
  formatLocalTime,
  formatMigrationDate,
  formatUtcTime,
  getLocalTimeZone,
  groupSlotsByLocalDay,
} from '@/components/layout/oci-migration-notice/migration-schedule/utils'
import {
  COMPANY_MIGRATION_SCHEDULE_QUERY,
  COMPANY_MIGRATION_SLOTS_QUERY,
  RESCHEDULE_MIGRATION_MUTATION,
} from '@/components/layout/oci-migration-notice/queries'
import { ToasterContent } from '@/components/sv/toaster-content'
import { Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { DialogClose, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { useMixpanel, useSuspenseQuery } from '@/lib/hooks'
import type {
  CompanyClusterMigrationScheduleSlotsQuery,
  CompanyClusterMigrationScheduleSlotsQueryVariables,
  RescheduleClusterMigrationMutation,
  RescheduleClusterMigrationMutationVariables,
} from '@/components/layout/oci-migration-notice/queries.types.gen'

const SLOT_LIMIT = 100

const NOTE_MAX_LENGTH = 2000

const RESCHEDULE_ERROR_MESSAGE =
  'We could not move your migration to this time. It may have just been taken, so please pick another one.'

type SlotPickerProps = {
  idCompany: string
  waveLabel: string
  onRescheduled: () => void
}

export const SlotPicker = ({
  idCompany,
  waveLabel,
  onRescheduled,
}: SlotPickerProps) => {
  const client = useApolloClient()
  const { track } = useMixpanel()
  const [isPending, startTransition] = useTransition()
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const { data } = useSuspenseQuery<
    CompanyClusterMigrationScheduleSlotsQuery,
    CompanyClusterMigrationScheduleSlotsQueryVariables
  >(COMPANY_MIGRATION_SLOTS_QUERY, {
    variables: { idCompany, limit: SLOT_LIMIT },
    fetchPolicy: 'network-only',
  })

  const [rescheduleMigration] = useMutation<
    RescheduleClusterMigrationMutation,
    RescheduleClusterMigrationMutationVariables
  >(RESCHEDULE_MIGRATION_MUTATION, {
    refetchQueries: [COMPANY_MIGRATION_SCHEDULE_QUERY],
    awaitRefetchQueries: true,
  })

  const days = groupSlotsByLocalDay(
    data?.companyClusterMigrationScheduleSlots ?? []
  )

  if (!days.length) {
    return (
      <>
        <Muted as="p" className="text-sm">
          There are no other free times right now. Leave a note for our team and
          we will find a time with you.
        </Muted>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        </DialogFooter>
      </>
    )
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    if (!selectedSlot) {
      return
    }

    const trimmedNote = note.trim()

    startTransition(async () => {
      try {
        await rescheduleMigration({
          variables: {
            idCompany,
            scheduledAt: selectedSlot,
            note: trimmedNote || undefined,
          },
        })
        track('OCI migration rescheduled', {
          wave: waveLabel,
          daysAhead: differenceInCalendarDays(
            new Date(selectedSlot),
            new Date()
          ),
          hasNote: Boolean(trimmedNote),
        })
        toast(
          <ToasterContent variant="success">
            Your migration moved to {formatMigrationDate(selectedSlot)}
          </ToasterContent>
        )
        onRescheduled()
      } catch {
        toast(
          <ToasterContent variant="error">
            {RESCHEDULE_ERROR_MESSAGE}
          </ToasterContent>
        )
        setSelectedSlot(null)
        client
          .refetchQueries({ include: [COMPANY_MIGRATION_SLOTS_QUERY] })
          .catch(() => undefined)
      }
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-w-0 flex-col gap-4">
      <Muted as="p" className="text-xs">
        Times are in your time zone ({getLocalTimeZone()}).
      </Muted>
      <div
        role="group"
        aria-label="Free times"
        className="flex max-h-72 flex-col divide-y overflow-y-auto rounded-md border"
      >
        {days.map(({ key, label, slots }) => {
          const daySlots = slots.map((slot) => slot.toISOString())
          const value =
            selectedSlot && daySlots.includes(selectedSlot) ? selectedSlot : ''

          return (
            <div
              key={key}
              className="grid gap-2 px-3 py-2 sm:grid-cols-[7rem_minmax(0,1fr)] sm:items-center sm:gap-3"
            >
              <span className="text-sm font-medium">{label}</span>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                spacing={1}
                className="flex flex-wrap gap-1.5"
                value={value}
                onValueChange={(nextValue) =>
                  setSelectedSlot(nextValue || null)
                }
                aria-label={label}
              >
                {slots.map((slot) => (
                  <ToggleGroupItem
                    key={slot.toISOString()}
                    value={slot.toISOString()}
                    aria-label={formatMigrationDate(slot)}
                    className="tabular-nums data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary/90 data-[state=on]:hover:text-primary-foreground"
                  >
                    {formatLocalTime(slot)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </div>
          )
        })}
      </div>
      <div aria-live="polite" className="min-h-5 text-sm">
        {selectedSlot ? (
          <>
            <span className="font-medium">
              {formatMigrationDate(selectedSlot)}
            </span>
            <span className="text-muted-foreground">
              {' · '}
              {formatUtcTime(selectedSlot)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">Pick a time above.</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reschedule-note">Note for our team (optional)</Label>
        <Textarea
          id="reschedule-note"
          value={note}
          maxLength={NOTE_MAX_LENGTH}
          placeholder="For example, why this time works better for you"
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="outline" disabled={isPending}>
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit" disabled={!selectedSlot || isPending}>
          {isPending ? <Loader className="animate-spin" /> : null}
          Move migration
        </Button>
      </DialogFooter>
    </form>
  )
}
